import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const DEPARTMENTS = [
  "admin",
  "editor",
  "graphic_designer",
  "ai",
  "marketer",
  "seo",
  "smm",
  "scriptwriter",
  "calling",
  "hr",
] as const;
const ROLES = ["admin", "moderator", "user"] as const;

async function assertAdmin(userId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (error) throw new Error(error.message);
  const roles = (data ?? []).map((r) => r.role);
  if (!roles.includes("admin")) throw new Error("Admin only");
}

/**
 * Returns caller capability for user creation.
 * - admin: full create (any role, any elevated flag)
 * - hr: can create ONLY plain users, no elevated flags — server enforces this
 * - otherwise: throws
 */
async function getCreateCapability(userId: string): Promise<"admin" | "hr"> {
  const [{ data: roleRows, error: rErr }, { data: prof, error: pErr }] = await Promise.all([
    supabaseAdmin.from("user_roles").select("role").eq("user_id", userId),
    supabaseAdmin.from("profiles").select("department").eq("id", userId).maybeSingle(),
  ]);
  if (rErr) throw new Error(rErr.message);
  if (pErr) throw new Error(pErr.message);
  const roles = (roleRows ?? []).map((r) => r.role);
  if (roles.includes("admin")) return "admin";
  if (prof?.department === "hr") return "hr";
  throw new Error("Only Admin or HR can create users");
}

const createUserSchema = z
  .object({
    email: z.string().email(),
    password: z.string().min(6),
    full_name: z.string().min(1),
    department: z.enum(DEPARTMENTS),
    role: z.enum(ROLES).optional(),
    monthly_target: z.coerce.number().int().min(0).max(1000).optional(),
    employee_id: z.string().regex(/^\d{4,}$/, "Employee ID must be numeric and at least 4 digits"),
    can_add_vault_entries: z.boolean().optional(),
    work_email: z.string().email().optional().or(z.literal("")).transform((v) => (v ? v : undefined)),
    phone: z.string().optional().or(z.literal("")).transform((v) => (v ? v : undefined)),
    gender: z.string().optional().or(z.literal("")).transform((v) => (v ? v : undefined)),
    date_of_birth: z.string().optional().or(z.literal("")).transform((v) => (v ? v : undefined)),
    joining_date: z.string().optional().or(z.literal("")).transform((v) => (v ? v : undefined)),
  })
  .transform((v) => ({
    ...v,
    monthly_target:
      v.monthly_target ?? (v.department === "editor" ? 80 : 0),
  }));

export const createUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => createUserSchema.parse(d))
  .handler(async ({ data, context }) => {
    const capability = await getCreateCapability(context.userId);

    // HR callers CANNOT escalate — hard-force role='user' and no elevated flags,
    // ignoring whatever the client tried to send.
    const effectiveRole: (typeof ROLES)[number] =
      capability === "admin" ? (data.role ?? "user") : "user";
    const effectiveVaultAdd =
      capability === "admin"
        ? (data.can_add_vault_entries ??
           (effectiveRole === "admin" || effectiveRole === "moderator"))
        : false;

    const { data: created, error } = await supabaseAdmin.auth.admin.createUser({
      email: data.email,
      password: data.password,
      email_confirm: true,
      user_metadata: {
        full_name: data.full_name,
        department: data.department,
        role: effectiveRole,
      },
    });
    if (error || !created.user) throw new Error(error?.message ?? "Failed");

    const profilePatch = {
      monthly_target: data.monthly_target,
      employee_id: data.employee_id,
      can_add_vault_entries: effectiveVaultAdd,
      status: "active" as const,
      is_active: true,
      is_team_lead: false,
      ...(data.work_email ? { work_email: data.work_email } : {}),
      ...(data.gender ? { gender: data.gender } : {}),
      ...(data.joining_date ? { joining_date: data.joining_date } : {}),
    };

    const { error: profErr } = await supabaseAdmin
      .from("profiles")
      .update(profilePatch)
      .eq("id", created.user.id);
    if (profErr) {
      await supabaseAdmin.auth.admin.deleteUser(created.user.id);
      if (profErr.code === "23505" || /unique/i.test(profErr.message)) {
        throw new Error("Employee ID already in use");
      }
      throw new Error(profErr.message);
    }

    if (data.phone || data.date_of_birth) {
      const privPatch = {
        ...(data.phone ? { phone: data.phone } : {}),
        ...(data.date_of_birth ? { date_of_birth: data.date_of_birth } : {}),
      };
      await supabaseAdmin
        .from("profiles_private")
        .update(privPatch)
        .eq("id", created.user.id);
    }


    // Ensure role rows match effective role exactly (HR always ends up with 'user')
    await supabaseAdmin.from("user_roles").delete().eq("user_id", created.user.id);
    await supabaseAdmin
      .from("user_roles")
      .insert({ user_id: created.user.id, role: effectiveRole });

    await supabaseAdmin.from("audit_log").insert({
      action_type: "user.create",
      admin_id: context.userId,
      target_user_id: created.user.id,
      after_data: {
        email: data.email,
        role: effectiveRole,
        department: data.department,
        created_by_capability: capability,
      },
    });
    return { id: created.user.id };
  });

const updateUserSchema = z.object({
  user_id: z.string().uuid(),
  full_name: z.string().optional(),
  department: z.enum(DEPARTMENTS).optional(),
  monthly_target: z.coerce.number().int().min(0).max(1000).optional(),
  status: z.enum(["active", "suspended", "inactive"]).optional(),
  role: z.enum(ROLES).optional(),
  can_add_vault_entries: z.boolean().optional(),
});

const deactivateSchema = z.object({ user_id: z.string().uuid() });

export const deactivateUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => deactivateSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    if (data.user_id === context.userId) {
      throw new Error("You cannot deactivate your own account.");
    }

    const { data: before } = await supabaseAdmin
      .from("profiles")
      .select("id, full_name, email, status, is_active")
      .eq("id", data.user_id)
      .maybeSingle();
    if (!before) throw new Error("User not found");

    const { error } = await supabaseAdmin
      .from("profiles")
      .update({
        status: "inactive",
        is_active: false,
        deactivated_at: new Date().toISOString(),
        deactivated_by: context.userId,
      })
      .eq("id", data.user_id);
    if (error) throw new Error(error.message);

    try {
      await supabaseAdmin.auth.admin.signOut(data.user_id, "global");
    } catch (e) {
      console.error("signOut failed", e);
    }

    await supabaseAdmin.from("audit_log").insert({
      action_type: "user_deactivated",
      admin_id: context.userId,
      target_user_id: data.user_id,
      before_data: before,
      after_data: { status: "inactive", is_active: false },
    });
    return { ok: true };
  });

export const reactivateUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => deactivateSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);

    const { data: before } = await supabaseAdmin
      .from("profiles")
      .select("id, full_name, email, status, is_active")
      .eq("id", data.user_id)
      .maybeSingle();
    if (!before) throw new Error("User not found");

    const { error } = await supabaseAdmin
      .from("profiles")
      .update({
        status: "active",
        is_active: true,
        deactivated_at: null,
        deactivated_by: null,
      })
      .eq("id", data.user_id);
    if (error) throw new Error(error.message);

    await supabaseAdmin.from("audit_log").insert({
      action_type: "user_reactivated",
      admin_id: context.userId,
      target_user_id: data.user_id,
      before_data: before,
      after_data: { status: "active", is_active: true },
    });
    return { ok: true };
  });

export const updateUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => updateUserSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);

    const { user_id, role, ...profilePatch } = data;

    const { data: before } = await supabaseAdmin
      .from("profiles")
      .select("*")
      .eq("id", user_id)
      .maybeSingle();

    if (Object.keys(profilePatch).length > 0) {
      const { error } = await supabaseAdmin
        .from("profiles")
        .update(profilePatch)
        .eq("id", user_id);
      if (error) throw new Error(error.message);
    }

    if (role) {
      // Replace role rows with the requested single role
      await supabaseAdmin.from("user_roles").delete().eq("user_id", user_id);
      await supabaseAdmin.from("user_roles").insert({ user_id, role });
    }

    // If the user is being deactivated, revoke all their sessions immediately.
    if (
      profilePatch.status &&
      profilePatch.status !== "active" &&
      before?.status === "active"
    ) {
      try {
        await supabaseAdmin.auth.admin.signOut(user_id, "global");
      } catch (e) {
        // Non-fatal: status is already flipped; the route guard will kick them on next click.
        console.error("signOut failed", e);
      }
    }

    await supabaseAdmin.from("audit_log").insert({
      action_type: "user.update",
      admin_id: context.userId,
      target_user_id: user_id,
      before_data: before ?? null,
      after_data: { ...profilePatch, role: role ?? null },
    });
    return { ok: true };
  });

const passwordSchema = z.object({
  user_id: z.string().uuid(),
  password: z.string().min(6),
});

export const resetUserPassword = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => passwordSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { error } = await supabaseAdmin.auth.admin.updateUserById(data.user_id, {
      password: data.password,
    });
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("audit_log").insert({
      action_type: "user.password_reset",
      admin_id: context.userId,
      target_user_id: data.user_id,
    });
    return { ok: true };
  });

const deleteSchema = z.object({ user_id: z.string().uuid() });

export const deleteUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => deleteSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    if (data.user_id === context.userId) throw new Error("Cannot delete yourself");
    const { error } = await supabaseAdmin.auth.admin.deleteUser(data.user_id);
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("audit_log").insert({
      action_type: "user.delete",
      admin_id: context.userId,
      target_user_id: data.user_id,
    });
    return { ok: true };
  });

const ALLOWED_PERMISSION_KEYS = new Set([
  "view_overview",
  "view_activity_feed",
  "search_users",
  "view_audit_log",
  "creatives_review",
  "task_assign",
  "send_notifications",
  "departments",
  "user_management",
  "lookups",
  "globals",
  "scope_own_department",
  "section_work_update",
  "section_work_boards",
  "section_calendar",
  "section_creatives",
  "section_creative_library",
  "section_today_work",
  "section_by_department",
  "section_by_employee",
  "section_sops",
  "section_chat",
  "section_vault",
  "section_send_notifications",
  "section_ads_performance",
]);


const permissionsSchema = z.object({
  user_id: z.string().uuid(),
  permissions: z.record(z.string(), z.unknown()).refine(
    (perms) => Object.keys(perms).every((k) => ALLOWED_PERMISSION_KEYS.has(k)),
    { message: "Unknown permission key" },
  ),
});

export const updateModeratorPermissions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => permissionsSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);

    const { data: before } = await supabaseAdmin
      .from("profiles")
      .select("moderator_permissions")
      .eq("id", data.user_id)
      .maybeSingle();

    const perms = data.permissions as never;
    const { error } = await supabaseAdmin
      .from("profiles")
      .update({ moderator_permissions: perms })
      .eq("id", data.user_id);
    if (error) throw new Error(error.message);

    await supabaseAdmin.from("audit_log").insert({
      action_type: "moderator.permissions_update",
      admin_id: context.userId,
      target_user_id: data.user_id,
      before_data: before?.moderator_permissions ?? null,
      after_data: perms,
    });
    return { ok: true };
  });
